import React from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Box,
  Chip,
  Button,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  SkipNext,
} from '@mui/icons-material';
import { ScheduleJob } from '../hooks/useScheduleData';
import { isToday } from '../utils/dateUtils';
import { getServiceFrequency } from '../utils/serviceDateUtils';
import InfoTip from './InfoTip';

interface PaymentStatus {
  status: string;
  [key: string]: any;
}

interface TodaysJobsProps {
  todaysJobs: ScheduleJob[];
  onCompleteJob: (job: ScheduleJob) => void;
  onSkipJob?: (job: ScheduleJob) => void;
  onRetryPayment: (jobId: string) => void;
  paymentStatuses: Map<string, PaymentStatus>;
  retryingPayment: string | null;
  formatServiceTypeName: (name?: string) => string;
  getStatusColor: (status: string) => 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

/**
 * Component for displaying Today's Jobs
 * Shows all jobs scheduled for today with actions to complete or retry payment
 */
const TodaysJobs: React.FC<TodaysJobsProps> = ({
  todaysJobs,
  onCompleteJob,
  onSkipJob,
  onRetryPayment,
  paymentStatuses,
  retryingPayment,
  formatServiceTypeName,
  getStatusColor,
}) => {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'inline-flex', alignItems: 'center' }}>
        Today's Jobs
        <InfoTip title="Stops for today. Complete marks done (and payment/next date); Skip means not done today without charging. Incomplete stops get a follow-up banner." />
      </Typography>
      {todaysJobs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No jobs scheduled for today
        </Typography>
      ) : (
        <List>
          {todaysJobs.map((job) => {
            const customerName = job.customerId 
              ? (typeof job.customerId === 'object' 
                  ? `${job.customerId.firstName || ''} ${job.customerId.lastName || ''}`.trim()
                  : 'Customer')
              : 'Customer';
            
            const isOnPaymentHold = job.isOnPaymentHold === true;
            const onHoldReason = (job as any).onHoldReason || null;
            
            // Determine if hold is due to payment failure vs payment method removal
            const isPaymentFailure = onHoldReason && (
              onHoldReason.toLowerCase().includes('payment failed') || 
              onHoldReason.toLowerCase().includes('card declined') ||
              onHoldReason.toLowerCase().includes('declined') ||
              onHoldReason.toLowerCase().includes('insufficient funds')
            );
            
            // CRITICAL: Check if job was completed today (even if status is 'scheduled' due to auto-rescheduling)
            // When a service is completed, backend:
            //   1. Sets status to 'completed' and sets completedDate to today
            //   2. Then auto-reschedules by setting status back to 'scheduled' and updating scheduledDate to next occurrence
            //   3. But completedDate remains set to today
            // So we check completedDate to determine if it was completed today, even if status is now 'scheduled'
            // This is a GENERAL fix that works for ALL completed services, not just this one case
            const completedDate = (job as any).completedDate;
            const wasCompletedToday = completedDate && isToday(completedDate);
            const isCompleted = job.status === 'completed' || wasCompletedToday;
            
            // Get the service type name for display
            const serviceTypeName = formatServiceTypeName(job.serviceType?.name || job.serviceType?.category || 'Service');
            
            return (
              <ListItem key={job._id} divider>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" fontWeight="medium">
                        {customerName}
                      </Typography>
                      {job.scheduledTime && (
                        <Typography variant="body2" color="text.secondary">
                          {job.scheduledTime}
                        </Typography>
                      )}
                      <Chip
                        label={isCompleted ? 'completed' : job.status}
                        color={getStatusColor(isCompleted ? 'completed' : job.status)}
                        size="small"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {(() => {
                          // For "Today's Jobs", show only the primary service category
                          // If serviceType.name contains multiple services (comma-separated),
                          // use the category to determine the primary service
                          const category = job.serviceType?.category;
                          const name = job.serviceType?.name || '';
                          
                          // If name contains comma, it means multiple services
                          // Use category to show only the primary service
                          if (name.includes(',') && category) {
                            const categoryMap: { [key: string]: string } = {
                              'mowing': 'Lawn Mowing/Trimming',
                              'fertilizing': 'Weed Control/Fertilizing',
                              'snow_removal': 'Snow Removal',
                              'other': 'Other Service'
                            };
                            return `${categoryMap[category] || category} (ID: ${job._id?.toString().slice(-8)})`;
                          }
                          
                          // Otherwise, show the formatted name
                          return `${formatServiceTypeName(name)} (ID: ${job._id?.toString().slice(-8)})`;
                        })()}
                      </Typography>
                      {isOnPaymentHold && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <Tooltip title={
                            isPaymentFailure
                              ? `Payment failed - ${onHoldReason || 'Payment processing failed. Service on hold until payment is resolved.'}`
                              : onHoldReason || 'No payment method on file - customer removed payment method'
                          }>
                            <Warning color="error" sx={{ fontSize: 16 }} />
                          </Tooltip>
                          <Chip 
                            label={isPaymentFailure ? 'Payment Hold' : 'No Payment Setup'} 
                            color="error" 
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </Box>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {job.address?.street}, {job.address?.city}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  {!isCompleted && (job.status === 'scheduled' || job.status === 'in-progress') && !isOnPaymentHold && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {onSkipJob &&
                        job.frequency !== 'per-instance' &&
                        job.serviceType?.category !== 'snow_removal' && (
                          <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<SkipNext />}
                            onClick={() => onSkipJob(job)}
                            title="Skip today without charging; recurring pattern stays"
                          >
                            Skip
                          </Button>
                        )}
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => onCompleteJob(job)}
                        title="Mark done and trigger payment / next schedule"
                      >
                        Complete
                      </Button>
                    </Box>
                  )}
                  {!isCompleted && (job.status === 'scheduled' || job.status === 'in-progress') && isOnPaymentHold && (
                    <Tooltip title="Cannot complete job - customer has no payment method on file">
                      <span>
                        <Button
                          variant="outlined"
                          color="error"
                          disabled
                          startIcon={<Warning />}
                        >
                          Payment Hold
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {isCompleted && (() => {
                    const payment = paymentStatuses.get(job._id?.toString() || '');
                    // Check if payment failed, is pending (not completed), or doesn't exist
                    // If payment doesn't exist, it means payment wasn't processed at all
                    // 'skip' status means payment was intentionally skipped (e.g., monthly subscription) - no retry needed
                    const needsPaymentRetry = payment 
                      ? (payment.status === 'failed' || payment.status === 'pending')
                      : true; // No payment record means payment wasn't processed
                    const isSkipped = payment?.status === 'skip';
                    const isPaymentUndetermined = isSkipped && payment?.type === 'seasonal_contract_payment_undetermined';
                    const isRetrying = retryingPayment === job._id?.toString();
                    
                    // SINGLE SOURCE OF TRUTH: Use getServiceFrequency helper to determine if this is a seasonal contract
                    // This ensures consistent detection across all components
                    const serviceCategory = job.serviceType?.category as 'mowing' | 'fertilizing' | 'snow_removal' | undefined;
                    const customer = typeof job.customerId === 'object' ? job.customerId : null;
                    
                    // Use getServiceFrequency helper - this checks all sources (acceptedBid, customer.servicePricing, job.frequency)
                    // For seasonal contracts, it returns the serviceFrequency (e.g., 'per-instance', 'weekly'), not 'seasonal-contract'
                    // So we need to check the original job data to determine if it's a seasonal contract
                    const serviceFrequency = serviceCategory 
                      ? getServiceFrequency(customer, serviceCategory, job)
                      : null;
                    
                    // Determine if this is a seasonal contract by checking the original frequency field
                    // or by checking acceptedBid/customerId for seasonal contract indicators
                    let isSeasonalContract = false;
                    let paymentSchedule: 'single-payment' | 'monthly' | undefined;
                    if ((job as any).frequency === 'seasonal-contract') {
                      isSeasonalContract = true;
                    }
                    if (job.acceptedBid?.servicePricing) {
                      const matchingServicePricing = job.acceptedBid.servicePricing.find((sp: any) => {
                        if (serviceCategory === 'mowing') return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
                        if (serviceCategory === 'fertilizing') return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
                        if (serviceCategory === 'snow_removal') return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
                        return false;
                      });
                      if (matchingServicePricing) {
                        isSeasonalContract = matchingServicePricing.isSeasonalContract === true || matchingServicePricing.frequency === 'seasonal-contract';
                        paymentSchedule = matchingServicePricing.seasonalContractDetails?.paymentSchedule;
                      }
                    }
                    if (!paymentSchedule && customer?.servicePricing) {
                      let customerPricingData: any = null;
                      if (serviceCategory === 'mowing' && customer.servicePricing.lawnMowingTrimming) {
                        customerPricingData = customer.servicePricing.lawnMowingTrimming;
                      } else if (serviceCategory === 'fertilizing' && customer.servicePricing.weedControlFertilizing) {
                        customerPricingData = customer.servicePricing.weedControlFertilizing;
                      } else if (serviceCategory === 'snow_removal' && customer.servicePricing.snowRemoval) {
                        customerPricingData = customer.servicePricing.snowRemoval;
                      }
                      if (customerPricingData) {
                        isSeasonalContract = isSeasonalContract || customerPricingData.frequency === 'seasonal-contract';
                        paymentSchedule = paymentSchedule ?? customerPricingData.seasonalContractDetails?.paymentSchedule;
                      }
                    }
                    
                    // Determine badge label from CONTRACT type (paymentSchedule), not payment record
                    // This fixes the case where a monthly contract was overcharged (bug) - we still show "Monthly Pay" because that's the contract
                    let completedLabel = "Completed";
                    if (isPaymentUndetermined) {
                      completedLabel = "Completed (Pending review)";
                    } else if (isSeasonalContract) {
                      if (paymentSchedule === 'monthly') {
                        completedLabel = "Completed (Monthly Pay)";
                      } else {
                        completedLabel = "Completed (One-Time Payment)";
                      }
                    }
                    
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {needsPaymentRetry && !isSkipped ? (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => onRetryPayment(job._id?.toString() || '')}
                            disabled={isRetrying}
                            sx={{ minWidth: 'auto', px: 1.5 }}
                          >
                            {isRetrying ? 'Processing...' : 'Retry Payment'}
                          </Button>
                        ) : (
                          <Chip 
                            label={completedLabel} 
                            color="success" 
                            size="small" 
                          />
                        )}
                      </Box>
                    );
                  })()}
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      )}
    </Paper>
  );
};

export default TodaysJobs;

